import React from "react";

// Source
import StoreRoute from "../../store/Route";
import StoreRole from "../../store/Role";
import StoreLocalization from "../../store/Localization";
import StorePreference from "../../store/Preference";
import * as HelperSrc from "../../HelperSrc";
import * as ModelPagination from "../../model/Pagination";
import * as ModelRoute from "../../model/Route";
import * as ModelCustomRoute from "../../model/custom/Route";
import * as ModelRole from "../../model/Role";
import * as ModelLocalization from "../../model/Localization";
import * as ModelCustomLocalization from "../../model/custom/Localization";
import * as ModelPreference from "../../model/Preference";
import * as ModelHelperSrc from "../../model/HelperSrc";
import * as ApiTask from "../../api/custom/Task";
import * as ModelTask from "../../model/custom/Task";
import ControllerAlert from "../Alert";

export default class ControllerTaskList {
    // Variable
    private storeRoute: ModelRoute.Istore<ModelCustomRoute.Idata>;
    private storageRouteData = React.useRef<ModelCustomRoute.Idata | undefined>(undefined);
    private storeRole: ModelRole.Istore;
    private storageRoleData = React.useRef<ModelRole.Idata | undefined>(undefined);
    private storeLocalization: ModelLocalization.Istore<ModelCustomLocalization.Idata>;
    private storageLocalizationData = React.useRef<ModelCustomLocalization.Idata | undefined>(undefined);
    private storePreference: ModelPreference.Istore<ModelPreference.Idata>;
    private storagePreferenceData = React.useRef<ModelPreference.Idata | undefined>(undefined);

    private controllerAlert: ControllerAlert;

    private elementName = React.useRef<HTMLInputElement | null>(null);
    private elementDay = React.useRef<HTMLInputElement | null>(null);
    private elementMemberId = React.useRef<HTMLInputElement | null>(null);
    private elementLatestUpdate = React.useRef<HTMLInputElement | null>(null);
    private elementLatestUpdateById = React.useRef<HTMLInputElement | null>(null);
    private elementViewMain = React.useRef<HTMLElement | null>(null);
    private selectOption = React.useRef<ModelTask.IselectOption | undefined>(undefined);

    private isRendered = React.useRef<boolean>(false);
    private idRemove = React.useRef(0);
    private filter = React.useRef<ModelPagination.CustomPagination.IfilterTask | undefined>(undefined);
    private dataMainList = React.useRef<ModelTask.Idata[] | undefined>(undefined);
    private language = React.useRef<string>("");

    getStorageRole: ModelRole.Idata | undefined;

    getApiLoadingList: Record<string, boolean>;
    setApiLoadingList: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;

    getSelectOption: ModelTask.IselectOption | undefined;
    setSelectOption: React.Dispatch<React.SetStateAction<ModelTask.IselectOption | undefined>>;

    getIsDialogOpen: boolean;
    setIsDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;

    getPagination: ModelPagination.Idata | undefined;
    setPagination: React.Dispatch<React.SetStateAction<ModelPagination.Idata | undefined>>;

    getFilter: ModelTask.Idata | undefined;
    setFilter: React.Dispatch<React.SetStateAction<ModelTask.Idata | undefined>>;

    getDataMainList: ModelTask.Idata[] | undefined;
    setDataMainList: React.Dispatch<React.SetStateAction<ModelTask.Idata[] | undefined>>;

    getLanguage: string;
    setLanguage: React.Dispatch<React.SetStateAction<string>>;

    getElementDayValue: string;
    setElementDayValue: React.Dispatch<React.SetStateAction<string>>;

    getElementMemberIdValue: string;
    setElementMemberIdValue: React.Dispatch<React.SetStateAction<string>>;

    getElementLatestUpdateByIdValue: string;
    setElementLatestUpdateByIdValue: React.Dispatch<React.SetStateAction<string>>;

    // Method
    constructor(isRendered: boolean) {
        this.storeRoute = new StoreRoute();
        this.storageRouteData.current = this.storeRoute.storageGet();
        this.storeRole = new StoreRole();
        this.storageRoleData.current = this.storeRole.storageGet();
        this.storeLocalization = new StoreLocalization();
        this.storageLocalizationData.current = this.storeLocalization.storageGet();
        this.storePreference = new StorePreference();
        this.storagePreferenceData.current = this.storePreference.storageGet();

        this.controllerAlert = new ControllerAlert(isRendered);

        this.isRendered.current = isRendered;

        const [getStorageRole] = React.useState<ModelRole.Idata | undefined>(this.storageRoleData.current);
        this.getStorageRole = getStorageRole;

        const [getApiLoadingList, setApiLoadingList] = React.useState<Record<string, boolean>>({});
        this.getApiLoadingList = getApiLoadingList;
        this.setApiLoadingList = setApiLoadingList;

        const [getSelectOption, setSelectOption] = React.useState<ModelTask.IselectOption | undefined>(undefined);
        this.getSelectOption = getSelectOption;
        this.setSelectOption = setSelectOption;

        const [getIsDialogOpen, setIsDialogOpen] = React.useState(false);
        this.getIsDialogOpen = getIsDialogOpen;
        this.setIsDialogOpen = setIsDialogOpen;

        const [getPagination, setPagination] = React.useState<ModelPagination.Idata | undefined>(undefined);
        this.getPagination = getPagination;
        this.setPagination = setPagination;

        const [getFilter, setFilter] = React.useState<ModelTask.Idata | undefined>(undefined);
        this.getFilter = getFilter;
        this.setFilter = setFilter;

        const [getDataMainList, setDataMainList] = React.useState<ModelTask.Idata[] | undefined>(this.dataMainList.current);
        this.getDataMainList = getDataMainList;
        this.setDataMainList = setDataMainList;

        const [getLanguage, setLanguage] = React.useState<string>(this.language.current);
        this.getLanguage = getLanguage;
        this.setLanguage = setLanguage;

        const [getElementDayValue, setElementDayValue] = React.useState<string>("");
        this.getElementDayValue = getElementDayValue;
        this.setElementDayValue = setElementDayValue;

        const [getElementMemberIdValue, setElementMemberIdValue] = React.useState<string>("");
        this.getElementMemberIdValue = getElementMemberIdValue;
        this.setElementMemberIdValue = setElementMemberIdValue;

        const [getElementLatestUpdateByIdValue, setElementLatestUpdateByIdValue] = React.useState<string>("");
        this.getElementLatestUpdateByIdValue = getElementLatestUpdateByIdValue;
        this.setElementLatestUpdateByIdValue = setElementLatestUpdateByIdValue;
    }

    initializeHook = (): void => {
        React.useEffect(() => {
            HelperSrc.domObserver(".view_task_list", () => {
                this.initializeHtmlElement();
            });

            this.apiSelectOption();

            this.apiDataList();
        }, []);

        React.useEffect(() => {
            this.storageRouteData.current = this.storeRoute.storageGet();
            this.storageRoleData.current = this.storeRole.storageGet();
            this.storageLocalizationData.current = this.storeLocalization.storageGet();
        }, [this.storageRouteData.current, this.storageRoleData.current, this.storageLocalizationData.current]);

        React.useEffect(() => {
            this.storagePreferenceData.current = this.storePreference.storageGet();

            if (this.storagePreferenceData.current) {
                const initialLanguage = this.language.current;

                if (this.language.current !== this.storagePreferenceData.current.language) {
                    this.language.current = this.storagePreferenceData.current.language;
                    this.setLanguage(this.language.current);

                    if (initialLanguage !== "") {
                        this.apiSelectOption();
                    }
                }
            }
        }, [this.storagePreferenceData.current]);
    };

    apiDataList = (isFilter = false): void => {
        this.filterParameter(isFilter);

        if (this.filter.current) {
            this.setApiLoadingList((prev) => ({
                ...prev,
                dataList: true
            }));

            ApiTask.dataList(this.filter.current, this.filter.current.sort_order, this.filter.current.page, (response) => {
                this.setApiLoadingList((prev) => ({
                    ...prev,
                    dataList: false
                }));

                if (response instanceof Error || response.message.error) {
                    if (response instanceof Error) {
                        HelperSrc.writeLog("error", response.message);
                    } else {
                        this.controllerAlert.show("error", response.message.error, false);
                    }

                    return;
                }

                this.setPagination(response.pagination);

                this.dataMainList.current = response.pagination.data as unknown as ModelTask.Idata[];
                this.setDataMainList(this.dataMainList.current);

                this.controllerAlert.show("success", response.message.success);
            });
        }
    };

    clickCreate = (): void => {
        if (this.storageRouteData.current) {
            HelperSrc.routerHistoryPush(this.storageRouteData.current.task_profile.path);
        }
    };

    clickFilterApply = (): void => {
        this.apiDataList(true);
    };

    clickItem = (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, data: ModelTask.Idata): void => {
        const element = event.target as HTMLElement;
        const tagName = element.tagName;

        if (this.storageRouteData.current && tagName !== "path") {
            HelperSrc.routerHistoryPush(this.storageRouteData.current.task_profile.path, data as unknown as Record<string, unknown>);
        }
    };

    clickRemove = (event: React.MouseEvent<SVGSVGElement, MouseEvent>, id: number): void => {
        event.stopPropagation();

        this.idRemove.current = id;

        this.setIsDialogOpen(true);
    };

    clickDialogRemoveConfirm = (): void => {
        if (this.idRemove.current > 0) {
            this.setApiLoadingList((prev) => ({
                ...prev,
                removeData: true
            }));

            ApiTask.removeData(this.idRemove.current, (response) => {
                this.setApiLoadingList((prev) => ({
                    ...prev,
                    removeData: false
                }));

                this.setIsDialogOpen(false);

                if (response instanceof Error || response.message.error) {
                    if (response instanceof Error) {
                        HelperSrc.writeLog("error", response.message);
                    } else {
                        this.controllerAlert.show("error", response.message.error, false);
                    }

                    return;
                }

                this.controllerAlert.show("success", response.message.success);

                this.apiDataList();
            });
        }
    };

    clickDialogRemoveCancel = (): void => {
        this.setIsDialogOpen(false);
    };

    private initializeHtmlElement = (): void => {
        if (!this.elementViewMain.current) {
            this.elementName.current = document.querySelector<HTMLInputElement>("[name='name']");
            this.elementDay.current = document.querySelector<HTMLInputElement>("[name='day']");
            this.elementMemberId.current = document.querySelector<HTMLInputElement>("[name='member_id']");
            this.elementLatestUpdate.current = document.querySelector<HTMLInputElement>("[name='latest_update']");
            this.elementLatestUpdateById.current = document.querySelector<HTMLInputElement>("[name='latest_update_by_id']");
            this.elementViewMain.current = document.querySelector<HTMLElement>(".view_task_list");
        }
    };

    private apiSelectOption = (): void => {
        this.setApiLoadingList((prev) => ({
            ...prev,
            selectOption: true
        }));

        ApiTask.selectOption((response) => {
            this.setApiLoadingList((prev) => ({
                ...prev,
                selectOption: false
            }));

            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                return;
            }

            this.selectOption.current = response.data.result as unknown as ModelTask.IselectOption;

            this.setSelectOption(this.selectOption.current);
        });
    };

    private filterParameter = (isFilter: boolean): void => {
        const pageUrl = HelperSrc.urlVariable("page") && !isFilter ? HelperSrc.urlVariable("page") : "1";
        const sortOrderUlr = HelperSrc.urlVariable("sort_order") && !isFilter ? HelperSrc.urlVariable("sort_order") : "desc";
        const nameUrl = HelperSrc.urlVariable("name") && !isFilter ? HelperSrc.urlVariable("name") : "";
        const dayUrl = HelperSrc.urlVariable("day") && !isFilter ? HelperSrc.urlVariable("day") : "";
        const memberIdUrl = HelperSrc.urlVariable("member_id") && !isFilter ? HelperSrc.urlVariable("member_id") : "";
        const latestUpdateUrl = HelperSrc.urlVariable("latest_update") && !isFilter ? HelperSrc.urlVariable("latest_update") : "";
        const latestUpdateByIdUrl = HelperSrc.urlVariable("latest_update_by_id") && !isFilter ? HelperSrc.urlVariable("latest_update_by_id") : "";

        const nameResult = this.elementName.current && this.elementName.current.value !== nameUrl ? this.elementName.current.value : nameUrl;
        const dayResult = this.elementDay.current && this.elementDay.current.value !== dayUrl ? this.elementDay.current.value : dayUrl;
        const memberIdResult =
            this.elementMemberId.current && this.elementMemberId.current.value !== memberIdUrl ? this.elementMemberId.current.value : memberIdUrl;
        const latestUpdateResult =
            this.elementLatestUpdate.current && this.elementLatestUpdate.current.value !== latestUpdateUrl
                ? this.elementLatestUpdate.current.value
                : latestUpdateUrl;
        const latestUpdateByIdResult =
            this.elementLatestUpdateById.current && this.elementLatestUpdateById.current.value !== latestUpdateByIdUrl
                ? this.elementLatestUpdateById.current.value
                : latestUpdateByIdUrl;

        this.filter.current = {
            page: pageUrl,
            sort_order: sortOrderUlr,
            name: nameResult as string & ModelHelperSrc.IvalidationForm,
            day: dayResult as string & ModelHelperSrc.IvalidationForm,
            member_id: memberIdResult as unknown as number & ModelHelperSrc.IvalidationForm,
            latest_update: latestUpdateResult as string & ModelHelperSrc.IvalidationForm,
            latest_update_by_id: latestUpdateByIdResult as unknown as number & ModelHelperSrc.IvalidationForm
        } as ModelPagination.CustomPagination.IfilterTask;
        this.setFilter(this.filter.current);

        if (this.storageRouteData.current) {
            HelperSrc.routerHistoryPush(
                `${this.storageRouteData.current.task_list.path}?page=${this.filter.current.page}&sort_order=${this.filter.current.sort_order}&` +
                    `name=${this.filter.current.name}&` +
                    `day=${this.filter.current.day}&` +
                    `member_id=${this.filter.current.member_id}&` +
                    `latest_update=${this.filter.current.latest_update}&` +
                    `latest_update_by_id=${this.filter.current.latest_update_by_id}`,
                {},
                "",
                true
            );
        }
    };
}
